/**
 * Responsive dock adopts existing components without resetting navigation.
 * Flex children keep navigation, fuel, and arrival information from overlapping.
 * Original DOM positions are restored when the viewport returns to portrait.
 */

import { store, select } from '../core/store';
import { formatClock, formatSpeed, speedUnitLabel } from '../core/format';

export type DockLayout = 'bottom' | 'side' | null;

interface DockParts {
  fuel: HTMLElement;
  sign: HTMLElement;
  fleet: HTMLElement;
  hud: HTMLElement;
  idle: HTMLElement;
}

/** Write only on change - same reasoning as the HUD's setText. */
function setText(el: HTMLElement, value: string): void {
  if (el.textContent !== value) el.textContent = value;
}

export class CarDock {
  readonly el: HTMLElement;
  private layout: DockLayout = null;
  /** Original position of every adopted element, for exact restore. */
  private markers = new Map<HTMLElement, Comment>();
  private clockTimer: number | null = null;
  private clockEl: HTMLElement;
  private speedEl: HTMLElement;
  private speedUnitEl: HTMLElement;
  private scrollEl: HTMLElement;
  private hudSlot: HTMLElement;
  private idleSlot: HTMLElement;

  constructor(
    parent: HTMLElement,
    private parts: DockParts,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'cardock';
    this.el.hidden = true;
    this.el.innerHTML = `
      <div class="cardock__scroll"></div>
      <div class="cardock__hudslot"></div>
      <div class="cardock__row">
        <div class="cardock__meta">
          <div class="cardock__speed">
            <span class="cardock__speed-value">--</span>
            <span class="cardock__speed-unit">km/h</span>
          </div>
          <div class="cardock__clock">--:--</div>
        </div>
      </div>
      <div class="cardock__idleslot"></div>`;
    parent.appendChild(this.el);

    this.clockEl = this.el.querySelector('.cardock__clock')!;
    this.speedEl = this.el.querySelector('.cardock__speed-value')!;
    this.speedUnitEl = this.el.querySelector('.cardock__speed-unit')!;
    this.scrollEl = this.el.querySelector('.cardock__scroll')!;
    this.hudSlot = this.el.querySelector('.cardock__hudslot')!;
    this.idleSlot = this.el.querySelector('.cardock__idleslot')!;

    // Live speed straight off the GPS fix - glanceable even when idle, which
    // the HUD's little dial (navigation only) never covered.
    select(
      (s) => s.fix,
      () => this.refreshSpeed(),
    );

    /*
     * Anything floating above the bottom bar (re-centre, version tag) offsets
     * itself by the bar's real height - measured, because the bar grows when
     * the arrival bar joins it, exactly the class of guess that broke the old
     * landscape layout.
     */
    new ResizeObserver(() => this.measure()).observe(this.el);
  }

  setLayout(layout: DockLayout): void {
    if (layout === this.layout) return;
    this.release();
    this.layout = layout;
    this.el.classList.toggle('cardock--bottom', layout === 'bottom');
    this.el.classList.toggle('cardock--side', layout === 'side');
    this.el.hidden = layout === null;

    if (layout === 'side') {
      // The maneuver is the only time-critical item, so it never gets pushed
      // down by a fuel suggestion that happened to arrive first.
      this.adopt(this.parts.sign, this.scrollEl);
      this.adopt(this.parts.fuel, this.scrollEl);
      this.adopt(this.parts.fleet, this.scrollEl);
      this.adopt(this.parts.hud, this.hudSlot);
    } else if (layout === 'bottom') {
      this.adopt(this.parts.hud, this.hudSlot);
      this.adopt(this.parts.idle, this.idleSlot);
      this.adopt(this.parts.fuel, this.el);

    }

    if (layout !== null) this.startClock();
    else this.stopClock();
    this.measure();
  }

  private adopt(el: HTMLElement, slot: HTMLElement): void {
    const marker = document.createComment('cardock-home');
    el.parentNode?.insertBefore(marker, el);
    this.markers.set(el, marker);
    slot.appendChild(el);
  }

  private release(): void {
    for (const [el, marker] of this.markers) {
      marker.parentNode?.insertBefore(el, marker);
      marker.remove();
    }
    this.markers.clear();
  }

  private measure(): void {
    const h = this.layout === 'bottom' && !this.el.hidden ? this.el.offsetHeight : 0;
    document.documentElement.style.setProperty('--dock-h', `${Math.round(h)}px`);
  }

  private refreshSpeed(): void {
    const s = store.get();
    setText(this.speedEl, formatSpeed(s.fix ? (s.fix.speed ?? 0) : null, s.settings.units));
    setText(this.speedUnitEl, speedUnitLabel(s.settings.units));
  }

  private startClock(): void {
    if (this.clockTimer !== null) return;
    const tick = (): void => setText(this.clockEl, formatClock(Date.now()));
    tick();
    // Half-minute tick: cheap, and the clock is never more than 30s stale.
    this.clockTimer = window.setInterval(tick, 30_000);
  }

  private stopClock(): void {
    if (this.clockTimer !== null) clearInterval(this.clockTimer);
    this.clockTimer = null;
  }
}
